import type { GeoArea, GeoPt } from './types'
import type { BorderAppearance } from './mapAppearance'
import { PROJECTED_WORLD_METRES, TILE_PX } from './mapLines'
import { PAGE_PX_PER_MM } from './printLayout'
import { TARGET_PRINT_DPI } from './printPaper'
import { MIN_RING_POINTS, ringsToPolygons, unionedRings } from './polygonUnion'

// ── The app's constants, verbatim ───────────────────────────────────────────
//
// From the mobile reference renderer's own statics and the DomainColors it
// paints with. `subarea` deliberately equals `hunt`: the app draws a sub-area at
// the hunt border's own base width and separates the two by hue and alpha, not
// by weight. Under a wash (reverse or filled) the app thins the hunt outline to
// a 6 core with an 18 band: the wash carries the visual weight there.
export const GROUND_BASE_WIDTH = { hunt: 12, halo: 36, washCore: 6, washBand: 18, subarea: 12, forbidden: 6 } as const
export const GROUND_HALO_RATIO = GROUND_BASE_WIDTH.halo / GROUND_BASE_WIDTH.hunt
/** The app withholds the halo at or below this zoom. Strict `>`. */
export const GROUND_HALO_MIN_ZOOM = 11
export const GROUND_HALO_OPACITY = 0.5

/** The hunt outline's core and outside band bases for a border style. */
export function huntOutlineWidths(style: BorderAppearance): { core: number; band: number } {
  return style === 'standard'
    ? { core: GROUND_BASE_WIDTH.hunt, band: GROUND_BASE_WIDTH.halo }
    : { core: GROUND_BASE_WIDTH.washCore, band: GROUND_BASE_WIDTH.washBand }
}

export const GROUND_DEFAULT_COLOR = '#3E68FF'
export const GROUND_HIGHLIGHT_COLOR = '#FF681F'
export const SUBAREA_STROKE = '#FF00FE'
/** The alpha byte of the app's sub-area colour, 0x94. */
export const SUBAREA_STROKE_OPACITY = 148 / 255
export const FORBIDDEN_FILL = '#BA1A1A66'
export const FORBIDDEN_STROKE = '#BA1A1A'

export const REVERSE_WASH_FILL = 'rgba(186,26,26,0.4)'
export const FILLED_WASH_FILL = 'rgba(0,127,0,0.3)'
/**
 * Outlines for the appearance panel's two wash swatches.
 *
 * The reverse treatment paints no stroke on the map, so its swatch borrows the
 * forbidden edge — the colour a user actually sees beside that wash. The panel's
 * own literals were a different red entirely, so the swatch advertised a colour
 * the map never draws.
 */
export const REVERSE_WASH_STROKE = FORBIDDEN_STROKE
export const FILLED_WASH_STROKE = '#007F00'

/**
 * Multiplies an OPACITY — never a width — for the editor's inactive-tab dim.
 * Dimming by width gives one ground two width tables, which is the mechanism
 * that let the editor and the read-only map drift apart in the first place.
 */
export const GROUND_DIM_FACTOR = 0.4

/**
 * The clickable width of a border in real CSS pixels, independent of zoom.
 *
 * 21 is what the editor already gave a border at its own base zoom, where the old
 * expression resolved to about 21.2 CSS px. That expression grew and shrank with
 * zoom, so a border was easier to click zoomed in than out; pinning it keeps the
 * easiest case and removes the variation. Deriving the target from the stroke
 * alone would land near 12 px and make a border harder to hit than the drawn line
 * beside it, whose own target has a comparable floor.
 */
export const GROUND_HIT_TARGET_CSS_PX = 21

// ── The width law ──────────────────────────────────────────────────────────
//
//   rendered_px = base × thicknessScale × 2 ** (0.6 × zoom − 10.5)
//
// This is NOT the line law in mapLines.ts. A line responds as the SQUARE ROOT of
// the map scale; a hunting-ground border responds as scale**0.6. Across zoom
// 4→18 the two diverge by 2**(0.1×14) ≈ 2.6×, so lineStrokeWidthCssPx must never
// be called for an area outline, nor this for a line.
//
// The result is a REAL pixel size — the MapLibre paint expressions in
// mapGeoJson.ts consume it directly as `line-width` in CSS px. Leaving the
// width in viewBox units, the old SVG renderer's mistake, is what made the
// same ground draw 8 px in a 1040×460 card and 14.8 px in a 1920×1000 one, at
// every zoom identically.
const GROUND_SCALE_SLOPE = 0.6
const GROUND_SCALE_OFFSET = 10.5

/**
 * The lowest zoom whose width response is used. A WEB DEVIATION from the app,
 * and the one number in this file a reviewer should challenge.
 *
 * The app's map opens ON a ground at zoom 13–14. Web frames a WHOLE ground into
 * a 460 px card, and the binding term for a square ground of side S km at
 * Swedish latitudes is z = log2(22884 / S): a 3–5 km ground opens at 12, a
 * 7–10 km one at 11, a 15–20 km one at 10. At zoom 11 the faithful law gives a
 * 0.80 px core and withholds the halo, i.e. a large club's border becomes a
 * sub-pixel hairline where it is 8 px today.
 *
 * So web clamps the zoom RESPONSE, not the width. Clamping the response keeps
 * the 12 / 12 / 6 / 36 hierarchy exactly proportional at every zoom, where a
 * per-width pixel floor would flatten a forbidden edge (base 6) onto a hunt
 * border (base 12) and destroy the distinction. 12 is the fitted zoom of a
 * median (~5 km) ground in this surface's own card, so the rule reads: never
 * thinner than the app draws at the zoom this surface actually opens at.
 */
export const GROUND_MIN_EFFECTIVE_ZOOM = 12

export function effectiveGroundZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) return GROUND_MIN_EFFECTIVE_ZOOM
  return Math.max(zoom, GROUND_MIN_EFFECTIVE_ZOOM)
}

export function groundScaleFactor(zoom: number): number {
  return 2 ** (GROUND_SCALE_SLOPE * effectiveGroundZoom(zoom) - GROUND_SCALE_OFFSET)
}

/** The app's rendered width, in real CSS pixels. */
export function groundStrokeCssPx(base: number, zoom: number, thicknessScale = 1): number {
  const ts = Number.isFinite(thicknessScale) ? thicknessScale : 1
  return base * ts * groundScaleFactor(zoom)
}

/**
 * The app's halo gate. INERT under the current GROUND_MIN_EFFECTIVE_ZOOM (12 is
 * above 11, so this is always true) and kept anyway: it is the app's rule, the
 * floor above it is a web deviation that may be lowered or removed, and encoding
 * the gate now means that day it is already correct rather than newly wrong.
 */
export function shouldDrawGroundHalo(zoom: number): boolean {
  return effectiveGroundZoom(zoom) > GROUND_HALO_MIN_ZOOM
}

// ── Colour ─────────────────────────────────────────────────────────────────

/**
 * `#RRGGBB`, or `#AARRGGBB` reduced to its RGB (SVG carries alpha separately).
 *
 * Anything else — a named colour, `rgb()`, a 3-digit shorthand, an unconverted
 * legacy value, `''`, `null` — is rejected so the caller falls through to a
 * default instead of emitting an attribute the renderer resolves to `none`,
 * which is how a malformed stored colour makes a border DISAPPEAR rather than
 * merely look wrong. Guarding here rather than in the DTO mapper protects every
 * surface at once.
 */
export function sanitizeGroundColor(value: string | null | undefined): string | null {
  const m = /^#?([0-9a-f]{6}|[0-9a-f]{8})$/i.exec((value ?? '').trim())
  if (!m) return null
  const hex = m[1]
  return `#${(hex.length === 8 ? hex.slice(2) : hex).toUpperCase()}`
}

/**
 * One precedence rule, applied on every surface: an active hunt's highlight, then
 * the border's own colour, then the team's, then the app default.
 *
 * A border's own colour wins because a colour is chosen per border, so a team with
 * several areas can tell them apart. The team colour is what a border without one
 * of its own is drawn in.
 */
export function resolveHuntBorderColor(o: {
  areaColor?: string | null
  teamColor?: string | null
  highlighted?: boolean
}): string {
  if (o.highlighted) return GROUND_HIGHLIGHT_COLOR
  return (
    sanitizeGroundColor(o.areaColor) ??
    sanitizeGroundColor(o.teamColor) ??
    GROUND_DEFAULT_COLOR
  )
}

export type GroundLayer = 'hunt' | 'subarea' | 'forbidden'

// ── The shapes a layer draws ───────────────────────────────────────────────

export interface GroundShape {
  /** Stable React key for the shape. */
  key: string
  name: string
  /**
   * Outer rings first, then interior holes. Never empty; never a ring under
   * MIN_RING_POINTS.
   */
  rings: GeoPt[][]
  /** How many leading entries of {@link rings} are outers rather than holes. */
  outerCount: number
  /** The shape's stored colour, unsanitised. The hunt layer reads it; others ignore it. */
  color?: string
  areaIds: string[]
}

/** Bucket key for an area with no colour of its own. Not a valid colour, so it
 *  cannot collide with one. */
const NO_COLOUR_BUCKET = 'no-colour'

/**
 * How the app groups each layer, and what a bucket means.
 *
 *   hunt       one bucket per stored colour; every area without one shares the
 *              default bucket
 *   subarea    one bucket per group id, falling back to the area's own id
 *   forbidden  one bucket for the whole layer
 *
 * The hunt layer buckets by COLOUR rather than copying the app's styled/unstyled
 * split, and that is a deliberate deviation. On web "has a colour" carried no
 * styling intent: the editor stamped one on every property parcel and handed each
 * new hand-drawn border the next entry from its palette — whose third entry is the
 * default blue. A styled/unstyled split would therefore leave the reported seam
 * quilt exactly where it is, on the very data that produced it, while a
 * bucket-by-colour union merges it and still keeps two deliberately
 * different-coloured grounds apart.
 */
/**
 * How many of an area's rings are outers. An area that does not say is all outers:
 * that is what every hand-drawn and single-ring area is, and it is the safer of the
 * two mistakes — reading a hole as an outer fills a lake in, while reading an outer
 * as a hole erases a whole parcel.
 */
export function outerRingCount(area: Pick<GeoArea, 'rings' | 'outerCount'>): number {
  return area.outerCount ?? area.rings.length
}

function bucketKeyFor(layer: GroundLayer, area: GeoArea): string {
  if (layer === 'forbidden') return 'all'
  if (layer === 'subarea') return area.areaGroup || area.id
  return sanitizeGroundColor(area.color) ?? NO_COLOUR_BUCKET
}

/**
 * The only way any surface obtains rings to draw.
 *
 * Degenerate rings are dropped here rather than at each site, and the areas of a
 * layer are unioned the way the app unions them, so a read-only map stops drawing
 * every internal parcel seam at full weight with each parcel's halo stacked along
 * it.
 *
 * A disjoint union and an enclosed hole are both kept: one shape carrying several
 * outer rings, and the gaps as holes. There is deliberately NO per-member
 * fallback path, because that path is what the seam quilt came back through.
 */
export function groundShapes(
  areas: readonly GeoArea[] | undefined,
  layer: GroundLayer = 'hunt',
): GroundShape[] {
  const drawable = (areas ?? []).filter((a) => a.rings?.some((r) => r.length >= MIN_RING_POINTS))
  if (!drawable.length) return []

  // First-seen order, so a shape does not jump position when a member changes.
  const buckets = new Map<string, GeoArea[]>()
  for (const area of drawable) {
    const key = bucketKeyFor(layer, area)
    const bucket = buckets.get(key)
    if (bucket) bucket.push(area)
    else buckets.set(key, [area])
  }

  return [...buckets.entries()]
    .flatMap(([key, members]): GroundShape[] => {
      // One entry per PART, across every member: an imported ground is often several
      // parcels, and one with an excluded lake carries that lake as a hole belonging
      // to a particular parcel. The union has to see the holes, or a lake inside one
      // parcel is filled back in by the parcel beside it.
      //
      // The union therefore runs on the part count rather than the member count: a
      // single member that is itself two touching parcels has a seam to dissolve just
      // as much as two members do.
      const polys = members.flatMap((m) => ringsToPolygons(m.rings, outerRingCount(m)))
      // The shared resolver, which the editor's display grouping also goes through:
      // merged where the clipper can, every part kept where it cannot.
      const { rings, outerCount } = unionedRings(polys)
      // Nothing enclosing anything: every ring the bucket had was a hole with no
      // outer to belong to, or too short to enclose. There is no shape to draw.
      if (!rings.length) return []
      return [{
        key: members.length > 1 ? key : members[0].id,
        // The first non-empty name and the first colour across the members, as the
        // app resolves both: the area a selection starts from routinely carries
        // neither.
        name: members.find((m) => m.name)?.name ?? '',
        rings,
        outerCount,
        color: members.find((m) => m.color)?.color,
        areaIds: members.map((m) => m.id),
      }]
    })
}

// ── The same law, on paper ─────────────────────────────────────────────────

/**
 * The zoom at which the on-screen map shows the same ground per unit length as a
 * printed sheet at cartographic scale `scale`.
 *
 * This is the bridge between the two media, and it exists because the width law is a
 * function of ZOOM while the print server wants millimetres on paper. Rather than
 * inventing a second law for print — which is how a flat millimetre constant came to
 * be here in the first place — the printed width is the screen law evaluated at the
 * zoom the sheet is equivalent to.
 *
 * At scale S one paper mm is S/1000 ground metres, so the sheet shows
 * `PAGE_PX_PER_MM · 1000/S` px per ground metre. The screen at zoom z, latitude φ shows
 * `mapPxPerProjectedMetre(z) / cos φ`. Equating and solving for z gives the expression
 * below.
 *
 * Latitude stays in the law deliberately. The same 1:50 000 sheet prints a thicker
 * boundary at 55°N than at 70°N — about a 1.35× spread for one cartographic scale —
 * and dropping `cos φ` was rejected because the BROWSER print path physically
 * magnifies the screen and therefore always produces the cos φ answer. Dropping it
 * would put the two print paths a factor of (cos φ)^0.6 apart, which is 2× at 70°N.
 *
 * Margins deliberately do not enter. Someone will look for `DEFAULT_PRINT_MARGIN_MM`
 * here: the cartographic scale alone fixes mm-per-ground-metre, and the margin only
 * decides how much ground fits on the sheet.
 */
export function groundEquivalentPrintZoom(opts: { scale: number; lat: number }): number {
  const cosPhi = Math.cos((opts.lat * Math.PI) / 180)
  return Math.log2(
    (PROJECTED_WORLD_METRES * 1000 * PAGE_PX_PER_MM * cosPhi) / (TILE_PX * opts.scale),
  )
}

/**
 * The thinnest stroke worth asking a printer for, in mm.
 *
 * One and a half dots at the target DPI. Below one dot a line renders as nothing or as
 * one dot depending on the device's rounding, which is unpredictable rather than thin —
 * and the law does reach there: a forbidden edge at thicknessScale 0.5, 1:50 000, 70°N
 * lands under a single dot. It binds only around that combination and on nothing a
 * default map produces.
 */
export const PRINT_MIN_STROKE_MM = (1.5 * 25.4) / TARGET_PRINT_DPI

/**
 * A ground stroke's width in millimetres on paper.
 *
 * Closed form, worth stating because it is the honest summary of the whole change:
 * **width ∝ (cos φ / scale)^0.6**. Across the offered scale list the correct width
 * therefore varies by 10^0.6 ≈ 3.98×, which no flat constant can express. The 1.2 mm
 * this replaces is the app's width at zoom ≈ 15.16 — correct at about 1:8 000 at 60°N,
 * roughly a quarter too thin at 1:5 000, and 2.99× too heavy at 1:50 000, which is
 * exactly the sheet a user prints to cover a whole ground.
 *
 * The zoom floor applies, for the same reason it applies on screen and additionally so
 * that the two print paths agree where it binds — past roughly 1:72 000 at 60°N, which
 * is outside the offered scales except at the very top of Finland.
 */
export function groundStrokeMm(
  base: number,
  opts: { scale: number; lat: number; thicknessScale?: number },
): number {
  const mm =
    groundStrokeCssPx(base, groundEquivalentPrintZoom(opts), opts.thicknessScale) /
    PAGE_PX_PER_MM
  return Math.max(mm, PRINT_MIN_STROKE_MM)
}

/**
 * The mean of every vertex across a shape's rings — the anchor a label sits on.
 *
 * Averaged across ALL rings rather than the first, because a property group that
 * could not be unioned keeps one ring per parcel and the first parcel's centre
 * would put the group's name inside one corner of it.
 *
 * Null for an empty set, for the same reason polygonCentroid is: the origin is a
 * real coordinate, so returning it hides the caller's missing guard.
 */
export function polygonCentroidOfRings(rings: readonly (readonly GeoPt[])[]): GeoPt | null {
  let lat = 0
  let lon = 0
  let n = 0
  for (const ring of rings) {
    for (const p of ring) {
      lat += p.lat
      lon += p.lon
      n++
    }
  }
  return n ? { lat: lat / n, lon: lon / n } : null
}

